import { WORKFLOW_LIMITS, WORKFLOW_RULE_ACTION_TYPES, type WorkflowRuleActionType } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Select } from "@sagot/ui";
import { emptyConditions, freshStepId, isFacingStep, newStep } from "../definition-model";
import type { ActionStep, BranchStep, GoToStep, RulesStep, Step } from "../types";
import { AssignForm, LeadForm, NoteForm, NotifyForm, StatusForm, TagsForm } from "./action-forms";
import { ConditionEditor } from "./condition-editor";
import { ConnectorStepForm } from "./connector-step-form";
import { SetAttributeForm } from "./attribute-form";
import { EmailVisitorForm } from "./email-form";
import { FacingForm } from "./facing-forms";
import type { FlowContext } from "./flow-context";
import { HookWaitForm } from "./hook-wait-form";
import { LanguageForm } from "./language-form";
import { PathSelect } from "./path-select";
import { RunWorkflowForm } from "./run-workflow-form";
import { ApplySlaForm, SetPriorityForm } from "./sla-forms";
import { SnoozeForm } from "./snooze-form";
import { SummaryNoteForm } from "./summary-note-form";
import { SystemStepForm } from "./system/system-step-form";
import { isSystemStep } from "./system/system-model";
import { isServiceStep } from "../service/service-model";
import { ServiceStepForm } from "../service/service-step-form";
import { WaitForm } from "./wait-form";
import { WebhookForm, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";

export function ActionForm({ step, disabled, onChange, onConvert }: FormProps<ActionStep> & { onConvert?: (step: Step) => void }) {
  const change = onChange as (next: Step) => void;
  switch (step.type) {
    case "send_webhook":
      return <WebhookForm step={step} disabled={disabled} onChange={change} />;
    case "add_tags":
    case "remove_tags":
      return <TagsForm step={step} disabled={disabled} onChange={change} />;
    case "assign":
      return <AssignForm step={step} disabled={disabled} onChange={change} />;
    case "set_status":
      return <StatusForm step={step} disabled={disabled} onChange={change} />;
    case "add_note":
      return <NoteForm step={step} disabled={disabled} onChange={change} />;
    case "update_lead":
      return <LeadForm step={step} disabled={disabled} onChange={change} />;
    case "notify_staff":
      return <NotifyForm step={step} disabled={disabled} onChange={change} onConvert={onConvert} />;
    case "apply_sla":
      return <ApplySlaForm step={step} disabled={disabled} onChange={change} />;
    case "set_priority":
      return <SetPriorityForm step={step} disabled={disabled} onChange={change} />;
    case "email_visitor":
      return <EmailVisitorForm step={step} disabled={disabled} onChange={change} />;
    case "set_attribute":
      return <SetAttributeForm step={step} disabled={disabled} onChange={change} />;
    case "run_workflow":
      return <RunWorkflowForm step={step} disabled={disabled} onChange={change} />;
    case "set_language":
      return <LanguageForm step={step} disabled={disabled} onChange={change} />;
    case "add_summary_note":
      return <SummaryNoteForm step={step} disabled={disabled} onChange={change} />;
  }
}

function BranchForm({ step, disabled, onChange, paths, canAddPath }: FormProps<BranchStep> & FlowContext) {
  const { t } = useTranslation();
  const setBranch = (index: number, patch: Partial<BranchStep["branches"][number]>) => {
    onChange({ ...step, branches: step.branches.map((b, i) => (i === index ? { ...b, ...patch } : b)) });
  };
  return (
    <div className="space-y-4">
      {step.branches.map((branch, index) => (
        <section key={index} className="space-y-3 rounded-md border border-line p-3">
          <div className="flex items-end gap-2">
            <Field label={t(`${F}.branchLabel`)} className="flex-1">
              <Input maxLength={80} disabled={disabled} value={branch.label} onChange={(e) => { setBranch(index, { label: e.target.value }); }} />
            </Field>
            <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || step.branches.length === 1} aria-label={t(`${F}.removeBranch`)} onClick={() => { onChange({ ...step, branches: step.branches.filter((_, i) => i !== index) }); }}>
              <Icon name="trash" />
            </Button>
          </div>
          <ConditionEditor value={branch.when} disabled={disabled} onChange={(when) => { setBranch(index, { when }); }} />
          <PathSelect label={t(`${F}.goTo`)} value={branch.goto} paths={paths} canAddPath={canAddPath} disabled={disabled} onChange={(goto) => { setBranch(index, { goto: goto ?? branch.goto }); }} />
        </section>
      ))}
      {step.branches.length < WORKFLOW_LIMITS.branches ? (
        <Button type="button" variant="secondary" size="sm" disabled={disabled || paths.length === 0} onClick={() => { onChange({ ...step, branches: [...step.branches, { label: "", when: emptyConditions(), goto: paths[0] ?? "" }] }); }}>
          <Icon name="plus" />
          {t(`${F}.addBranch`)}
        </Button>
      ) : null}
      <PathSelect label={t(`${F}.elseGoTo`)} value={step.else_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.endHere`)} disabled={disabled} onChange={(else_goto) => { onChange({ ...step, else_goto }); }} />
    </div>
  );
}

function RulesForm({ step, disabled, onChange, taken }: FormProps<RulesStep> & FlowContext) {
  const { t } = useTranslation();
  const setRule = (index: number, patch: Partial<RulesStep["rules"][number]>) => {
    onChange({ ...step, rules: step.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  };
  const addAction = (index: number, type: WorkflowRuleActionType) => {
    const rule = step.rules[index];
    if (!rule) return;
    const used = new Set([...taken, ...step.rules.flatMap((r) => r.actions.map((a) => a.id))]);
    setRule(index, { actions: [...rule.actions, newStep(type, freshStepId(type, used)) as ActionStep] });
  };
  return (
    <div className="space-y-4">
      {step.rules.map((rule, index) => (
        <section key={index} className="space-y-3 rounded-md border border-line p-3">
          <div className="flex items-end gap-2">
            <Field label={t(`${F}.ruleLabel`)} className="flex-1">
              <Input maxLength={80} disabled={disabled} value={rule.label} onChange={(e) => { setRule(index, { label: e.target.value }); }} />
            </Field>
            <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || step.rules.length === 1} aria-label={t(`${F}.removeRule`)} onClick={() => { onChange({ ...step, rules: step.rules.filter((_, i) => i !== index) }); }}>
              <Icon name="trash" />
            </Button>
          </div>
          <ConditionEditor value={rule.when} disabled={disabled} onChange={(when) => { setRule(index, { when }); }} />
          <p className="label mb-0">{t(`${F}.ruleActions`)}</p>
          {rule.actions.map((action, a) => (
            <div key={action.id} className="space-y-2 rounded-md bg-surface-2 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold">{t(`engage.workflows.steps.${action.type}`)}</span>
                <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t("engage.workflows.builder.removeStep")} onClick={() => { setRule(index, { actions: rule.actions.filter((_, i) => i !== a) }); }}>
                  <Icon name="x" />
                </Button>
              </div>
              <ActionForm step={action} disabled={disabled} onChange={(next) => { setRule(index, { actions: rule.actions.map((x, i) => (i === a ? next : x)) }); }} />
            </div>
          ))}
          <Select aria-label={t(`${F}.addAction`)} className="h-9 w-auto text-xs" disabled={disabled} value="" onChange={(e) => { if (e.target.value) addAction(index, e.target.value as WorkflowRuleActionType); }}>
            <option value="">{t(`${F}.addAction`)}</option>
            {WORKFLOW_RULE_ACTION_TYPES.map((type) => <option key={type} value={type}>{t(`engage.workflows.steps.${type}`)}</option>)}
          </Select>
        </section>
      ))}
      <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { onChange({ ...step, rules: [...step.rules, { label: "", when: emptyConditions(), actions: [] }] }); }}>
        <Icon name="plus" />
        {t(`${F}.addRule`)}
      </Button>
    </div>
  );
}

/** The form for any step type. */
export function StepForm({ step, disabled, onChange, ...flow }: FormProps<Step> & FlowContext) {
  const { t } = useTranslation();
  if (step.type === "branch") return <BranchForm step={step} disabled={disabled} onChange={onChange} {...flow} />;
  if (step.type === "apply_rules") return <RulesForm step={step} disabled={disabled} onChange={onChange} {...flow} />;
  if (step.type === "go_to_path") {
    const go = step;
    return <PathSelect label={t(`${F}.goTo`)} value={go.path} paths={flow.paths} canAddPath={flow.canAddPath} disabled={disabled} onChange={(path) => { onChange({ ...go, path: path ?? go.path } satisfies GoToStep); }} />;
  }
  if (step.type === "wait") return <WaitForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} />;
  if (step.type === "wait_for_webhook") return <HookWaitForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} />;
  if (step.type === "snooze") return <SnoozeForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} />;
  if (step.type === "http_request") return <ConnectorStepForm step={step} disabled={disabled} onChange={onChange} paths={flow.paths} canAddPath={flow.canAddPath} facing={flow.facing} />;
  if (step.type === "end") return <p className="text-sm text-fg-muted">{t("engage.workflows.summaries.end")}</p>;
  if (isSystemStep(step)) return <SystemStepForm step={step} disabled={disabled} onChange={onChange} />;
  if (isServiceStep(step)) return <ServiceStepForm step={step} disabled={disabled} onChange={onChange} {...flow} />;
  if (isFacingStep(step)) return <FacingForm step={step} disabled={disabled} onChange={onChange} {...flow} />;
  return <ActionForm step={step} disabled={disabled} onChange={onChange} onConvert={onChange} />;
}
